import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Mail, Lock, KeyRound } from "lucide-react";
import AuthPageLayout from "./AuthPageLayout";
import LoginDownloadPanel from "./LoginDownloadPanel";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { setUserTokens } from "@/lib/userSession";

const inputClass =
  "w-full pl-11 pr-4 py-3.5 rounded-[12px] border border-[#E0E3EF] bg-white outline-none text-cm-navy text-[14px] focus:border-cm-blue focus:ring-2 focus:ring-cm-blue/10 transition disabled:bg-cm-surface";

export default function Login() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { email, password };
      if (needsTotp) payload.totp_code = totp;
      const { data } = await api.post("/api/auth/login", payload);
      if (data.access_token) setUserTokens(data.access_token, data.refresh_token);
      setUser(data.user, data.otp_verification_required);
      const otpRequired = data.otp_verification_required !== false;
      const needsOtpResume =
        otpRequired && (!data.user?.email_verified || !data.user?.mobile_verified);
      const needsMobileResume =
        !otpRequired &&
        (!data.user?.email_verified || !data.user?.mobile_verified) &&
        !data.user?.mobile_number;
      if (needsOtpResume || needsMobileResume) {
        nav("/register", { replace: true });
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "totp_required") {
        setNeedsTotp(true);
        setError("");
      } else if (detail && typeof detail === "object" && detail.code === "kyc_incomplete") {
        setError(
          detail.message ||
            "Please finish uploading your KYC documents to access your account.",
        );
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthPageLayout
      layoutTestId="login-layout"
      showBack
      navPrompt="New to CoinMBX?"
      navLinkLabel="Sign up"
      navLinkTo="/register"
      navLinkTestId="login-nav-signup"
    >
      <div
        className="rounded-[20px] border border-[var(--cm-border)] bg-white shadow-[0_32px_80px_-40px_rgba(29,45,63,0.2)] overflow-hidden grid lg:grid-cols-[1fr_380px]"
        data-testid="login-card"
      >
        {/* Form column */}
        <div className="p-8 lg:p-10">
          <h1 className="text-[32px] lg:text-[36px] font-extrabold text-cm-navy tracking-tight">
            Log in
          </h1>
          <p className="mt-2 text-[14px] text-cm-grey">
            Access spot and OTC from your CoinMBX account.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="login-email" className="text-[13px] font-semibold text-cm-navy">
                Email address
              </label>
              <div className="relative mt-1.5">
                <Mail
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cm-grey pointer-events-none"
                />
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={needsTotp}
                  data-testid="login-email"
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <label htmlFor="login-password" className="text-[13px] font-semibold text-cm-navy">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[12px] font-semibold text-cm-blue hover:underline"
                  data-testid="login-forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Lock
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cm-grey pointer-events-none"
                />
                <input
                  id="login-password"
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={needsTotp}
                  data-testid="login-password"
                  className={`${inputClass} pr-11`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cm-grey p-1 hover:text-cm-navy"
                  tabIndex={-1}
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {needsTotp && (
              <div className="cm-fade-up rounded-[12px] border border-cm-blue/20 bg-cm-lavender/40 p-4">
                <label htmlFor="login-totp" className="text-[13px] font-semibold text-cm-navy flex items-center gap-2">
                  <KeyRound size={16} className="text-cm-blue" />
                  Two-factor authentication
                </label>
                <input
                  id="login-totp"
                  inputMode="numeric"
                  pattern="[0-9A-Za-z]*"
                  maxLength={8}
                  required
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  data-testid="login-totp"
                  className={`${inputClass} mt-2 text-center tracking-[0.35em] font-mono`}
                  placeholder="000000"
                  autoFocus
                />
                <p className="mt-2 text-[12px] text-cm-grey">
                  Enter the 6-digit code from your authenticator app or a backup code.
                </p>
              </div>
            )}

            {error && (
              <div
                data-testid="login-error"
                className="text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-[12px] px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full py-3.5 rounded-[12px] bg-cm-blue hover:bg-cm-blue-deep transition text-white font-bold text-[15px] disabled:opacity-60 flex items-center justify-center gap-2 shadow-[0_12px_32px_-14px_rgba(2,44,237,0.55)]"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {needsTotp ? "Verify & log in" : "Log in"}
            </button>
          </form>

          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-[12px]">
            <Link to="/forgot-password" className="font-semibold text-cm-blue hover:underline">
              Forgot password?
            </Link>
            {needsTotp && (
              <Link to="/support" className="font-semibold text-cm-blue hover:underline">
                Lost 2FA access?
              </Link>
            )}
          </div>

          <p className="mt-6 text-[14px] text-cm-grey">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              data-testid="auth-to-register"
              className="text-cm-blue font-bold hover:underline"
            >
              Sign up
            </Link>
          </p>

          <p className="mt-8 text-[11px] text-cm-grey leading-relaxed border-t border-[var(--cm-border)] pt-6">
            CoinMBX complies with applicable Indian regulations for virtual digital assets. By
            logging in you agree to our{" "}
            <Link to="/terms" className="text-cm-blue font-semibold hover:underline">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-cm-blue font-semibold hover:underline">
              Privacy Policy
            </Link>
            .
          </p>

          {/* Download — mobile only (desktop uses right column) */}
          <div
            className="lg:hidden mt-8 rounded-[14px] border border-[var(--cm-border)] bg-cm-lavender/30 p-5"
            data-testid="login-download-mobile"
          >
            <p className="text-[13px] font-bold text-cm-navy mb-3">Download app</p>
            <p className="text-[12px] text-cm-grey mb-4">
              Get the Android APK from our website — no app store required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center justify-center gap-2 w-full h-[44px] rounded-[12px] bg-cm-blue text-white font-bold text-[13px]"
            >
              Go to download page
            </Link>
          </div>
        </div>

        {/* Download column — desktop */}
        <div className="hidden lg:block">
          <LoginDownloadPanel />
        </div>
      </div>
    </AuthPageLayout>
  );
}
